import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setServiceCategories,
  setServiceCategoriesLoading,
  setServiceCategoriesError,
} from "../redux/actions/dashboard";
import billingService from "../services/billingService";
import logger from "../utils/logger";


/**
 * Custom hook to manage service categories data with Redux caching
 * - Loads service categories only once per session
 * - Caches data in Redux store
 * - Automatically cleared on logout
 */
export const useServiceCategories = (type = "END_USER") => {
  const dispatch = useDispatch();
  const { serviceCategories, serviceCategoriesLoading, serviceCategoriesError } = useSelector(state => ({
    serviceCategories: state.dashboard?.serviceCategories || [],
    serviceCategoriesLoading: state.dashboard?.serviceCategoriesLoading || false,
    serviceCategoriesError: state.dashboard?.serviceCategoriesError || null,
  }));

  const loadServiceCategories = async () => {
    // If service categories are already loaded, don't fetch again
    if (serviceCategories.length > 0) {
      return serviceCategories;
    }

    // If already loading, don't start another request
    if (serviceCategoriesLoading) {
      return serviceCategories;
    }

    try {
      dispatch(setServiceCategoriesLoading(true));
      dispatch(setServiceCategoriesError(null));

      let data;

      // Try the new all-in-one endpoint first (requires auth)
      try {
        const response = await billingService.getAllServiceCategories({ type });
        if (response?.data?.data) {
          data = response.data.data;
        }
      } catch {
        // Fallback to public endpoint if the new one fails
      }

      // Fallback: use public endpoint
      if (!data) {
        const response = await billingService.getPublicServiceCategories({ limit: 500 });
        if (response && response.data && response.data.data) {
          data = response.data.data;
        } else {
          throw new Error("No service categories data received");
        }
      }

      if (data) {
        dispatch(setServiceCategories(data));
        return data;
      } else {
        throw new Error("No service categories data received");
      }
    } catch (error) {
      logger.error("Error loading service categories:", error);
      dispatch(setServiceCategoriesError(error.message || "Failed to load service categories"));
      return [];
    }
  };

  // Auto-load service categories when hook is first used
  useEffect(() => {
    if (serviceCategories.length === 0 && !serviceCategoriesLoading && !serviceCategoriesError) {
      loadServiceCategories();
    }
  }, []);

  return {
    serviceCategories,
    serviceCategoriesLoading,
    serviceCategoriesError,
    loadServiceCategories,
    hasServiceCategories: serviceCategories.length > 0,
  };
};
